import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, ArrowDown, Sparkles, FileText, ArrowRight } from 'lucide-react';
import { STATS } from '../data/siteData';

// Applies a subtle mouse-follow translation to the referenced element
function useMouseTilt(strength = 12) {
  const ref = useRef(null);

  useEffect(() => {
    // Handler defined inside the effect; empty deps besides `strength` are
    // intentional — only window-level listeners are attached on mount.
    const onMove = (e) => {
      const el = ref.current;
      if (!el) return;
      const x = (e.clientX / window.innerWidth - 0.5) * strength;
      const y = (e.clientY / window.innerHeight - 0.5) * strength;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [strength]);

  return ref;
}

function HeroStats() {
  return (
    <div className="hero-stats" data-testid="hero-stats">
      {STATS.map((s) => (
        <div key={s.label} className="hero-stat">
          <span className="hero-stat-value">{s.value}</span>
          <span className="hero-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function HeroRocket({ tiltRef }) {
  return (
    <div className="hero-rocket-wrap" ref={tiltRef} data-testid="hero-rocket">
      <div className="hero-rocket">
        <svg viewBox="0 0 200 260" className="rocket-svg" aria-hidden="true">
          {/* flame */}
          <g className="rocket-flame">
            <path d="M100 218 C88 240 92 252 100 262 C108 252 112 240 100 218 Z" fill="#FFB300" />
            <path d="M100 224 C93 238 96 247 100 254 C104 247 107 238 100 224 Z" fill="#FF5A00" />
          </g>
          {/* fins */}
          <path d="M74 150 C50 165 42 195 46 214 C62 202 74 192 80 178 Z" fill="#B30050" />
          <path d="M126 150 C150 165 158 195 154 214 C138 202 126 192 120 178 Z" fill="#B30050" />
          {/* body */}
          <path d="M100 8 C130 42 142 90 142 130 C142 168 126 196 100 210 C74 196 58 168 58 130 C58 90 70 42 100 8 Z" fill="#E60065" />
          {/* window */}
          <circle cx="100" cy="92" r="26" fill="#0B0B1E" />
          <circle cx="100" cy="92" r="19" fill="#ffffff" />
          <circle cx="94" cy="86" r="6" fill="#cfd8ff" />
          {/* stripe */}
          <path d="M66 158 C88 170 112 170 134 158 C130 178 118 196 100 206 C82 196 70 178 66 158 Z" fill="#ffffff" opacity="0.92" />
        </svg>
        <div className="rocket-smoke" aria-hidden="true">
          <span /><span /><span /><span /><span />
        </div>
      </div>
    </div>
  );
}

function HeroDecor() {
  return (
    <>
      <div className="hero-orbit hero-orbit-1" aria-hidden="true"><span className="orbit-dot" /></div>
      <div className="hero-orbit hero-orbit-2" aria-hidden="true"><span className="orbit-dot orbit-dot-pink" /></div>
      <div className="hero-planet" aria-hidden="true" />
      <div className="hero-nebula" aria-hidden="true" />
    </>
  );
}

export default function Hero() {
  const rocketRef = useMouseTilt();

  const goTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <header className="hero" id="top" data-testid="hero-section">
      <HeroDecor />

      <div className="hero-inner">
        <div className="hero-copy">
          <div className="hero-eyebrow" data-testid="hero-eyebrow">
            <Sparkles size={14} />
            <span>WE ARE BOOST! — DESIGN &amp; MARKETING</span>
          </div>

          <h1 className="hero-title" data-testid="hero-title">
            <span className="hero-line">GIVING YOUR</span>
            <span className="hero-line hero-line-pink">BRAND</span>
            <span className="hero-line">LIFT-OFF<span className="hero-bang">!</span></span>
          </h1>

          <p className="hero-sub" data-testid="hero-subtitle">
            AI systems, design, web, social, SEO &amp; automation — a full-stack
            creative crew engineering brands that break through the atmosphere.
          </p>

          <div className="hero-ctas">
            <button className="boost-btn boost-btn-primary boost-btn-lg" onClick={() => goTo('#contact')} data-testid="hero-cta-primary">
              <Rocket size={18} />
              Launch Your Brand
            </button>
            <button className="boost-btn boost-btn-ghost boost-btn-lg" onClick={() => goTo('#services')} data-testid="hero-cta-secondary">
              Explore Missions
              <ArrowDown size={18} />
            </button>
          </div>

          <Link to="/quote" className="hero-quote-link" data-testid="hero-quote-cta">
            <FileText size={15} /> Get an instant quote <ArrowRight size={15} />
          </Link>

          <HeroStats />
        </div>

        <HeroRocket tiltRef={rocketRef} />
      </div>

      <button className="hero-scroll-hint" onClick={() => goTo('#services')} aria-label="Scroll down" data-testid="hero-scroll-hint">
        <span className="scroll-hint-text">SCROLL TO IGNITE</span>
        <ArrowDown size={16} />
      </button>
    </header>
  );
}
